<template>
  <LeftGradientBlockLayoutBody>
    <template slot="left">
      <ValueInput v-model.trim="form_data.amount"
                  placeholder="0.00"
                  :label=" isIncomeDeal ? $t('valueIncomeLabel') : $t('valueExpenseLabel')"
                  :currency_id="instrumentCurrencyId"
                  :class="{'--error':$v.form_data.amount.$error }">
        <template v-slot:errors="">
          <div class="error" v-if="!$v.form_data.amount.required">{{ $t('errors.required') }}</div>
        </template>
      </ValueInput>
    </template>
    <template slot="right">
      <BaseSelect v-model="form_data.asset" :options="assetsList"
                  :select-label="$t('assetsLabel')"
                  :class="{'--error':$v.form_data.asset.$error}"
                  label="name"
                  track-by="id"
                  :allow-empty="false"
                  :disabled="isIncomeDeal && (deal_type === dealTypes.TYPE_INCOME_DIVIDEND || deal_type === dealTypes.TYPE_INCOME_COUPON || deal_type === dealTypes.TYPE_INCOME_AMORTIZATION)"
                  @input="form_data.asset = $event">
        <template v-slot:errors>
          <div class="error" v-if="$v.form_data.asset.$invalid">{{ $t('errors.required') }}</div>
        </template>
      </BaseSelect>
      <BaseTextarea v-model="form_data.note"
                    :label="$t('noteLabel')"
                    class="col-span-2"
                    placeholder=""/>
      <BaseButton :theme="isIncomeDeal ? 'green' : 'red'"
                  :loading="submitted"
                  @click="submit">
        {{ $t('buttonLabel') }}
      </BaseButton>
      <div class="flex items-center">
        <VueCtkDateTimePicker id="transaction-date"
                              class="inline"
                              v-model="form_data.date"
                              format="YYYY-MM-DD"
                              :formatted="dateFormatted"
                              :locale="$i18n.locale"
                              :auto-close="true"
                              :only-date="true"
                              :no-label="true"
                              :no-clear-button="true"
                              :no-button="true"
                              color="#2D4C5E"/>
        <VueCtkDateTimePicker id="transaction-time"
                              class="inline"
                              v-model="form_data.time"
                              format="HH:mm"
                              formatted="HH:mm"
                              :locale="$i18n.locale"
                              :only-time="true"
                              :no-clear-button="true"
                              :no-label="true"
                              :no-button="true"
                              color="#2D4C5E"/>
      </div>
    </template>
  </LeftGradientBlockLayoutBody>
</template>

<script>
import LeftGradientBlockLayoutBody
  from "@/components/layouts/cabinet/LeftGradientBlockLayout/LeftGradientBlockLayoutBody";
import ValueInput from "@/components/UI/BaseInput/ValueInput";
import BaseSelect from "@/components/UI/BaseSelect/BaseSelect";
import BaseButton from "@/components/UI/BaseButton";
import BaseTextarea from "@/components/UI/BaseTextarea";
import {decimal, required, requiredIf} from "vuelidate/lib/validators";
import {mapActions, mapGetters, mapState} from "vuex";
import {dealTypes} from "@/utilities/invest_deal_types";

export default {
  name: "ManualDealsEditForm",
  components: {
    BaseTextarea,
    BaseButton,
    BaseSelect,
    ValueInput,
    LeftGradientBlockLayoutBody
  },
  props: {
    deal_type: {
      type: Number,
      required: true
    },
    deal: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      form_data: {
        amount: null,
        asset: null,
        note: '',
        date: this.$moment().format('YYYY-MM-DD'),
        time: this.$moment().format('HH:mm'),
      },
      submitted: false
    }
  },
  computed: {
    ...mapState('investments/assets', ['grouped_assets_list']),
    ...mapState('investments/portfolios', ['current_portfolio']),
    ...mapGetters('cabinet/currencies', ['getCurrencyByCode']),
    dateFormatted() {
      const isToday = this.$moment(this.form_data.date, 'YYYY-MM-DD').startOf('day').unix() === this.$moment().startOf('day').unix()

      return isToday ? this.$t('today') : 'DD.MM.YYYY'
    },
    isIncomeDeal() {
      return !![dealTypes.TYPE_INCOME_DIVIDEND, dealTypes.TYPE_INCOME_COUPON, dealTypes.TYPE_INCOME_AMORTIZATION].includes(this.deal_type);
    },
    assetsList() {
      switch (this.deal_type) {
        case dealTypes.TYPE_INCOME_DIVIDEND:
          return this.grouped_assets_list.data.find(item => item.properties.type === 1)?.assets || [];
        case dealTypes.TYPE_INCOME_COUPON:
          return this.grouped_assets_list.data.find(item => item.properties.type === 2)?.assets || [];
        case dealTypes.TYPE_INCOME_AMORTIZATION:
          return this.grouped_assets_list.data.find(item => item.properties.type === 2)?.assets || [];
        default:
          return this.grouped_assets_list.data.find(item => item.properties.type === 4)?.assets || [];
      }
    },
    instrumentCurrencyId() {
      if (!!this.form_data.asset) {
        return this.getCurrencyByCode(this.form_data.asset.currency).id
      } else {
        return '';
      }
    },
  },
  validations: {
    form_data: {
      amount: {
        required,
        decimal,
        minValue: 0.01,
      },
      asset: {
        required: requiredIf(function () {
          return this.deal_type !== dealTypes.TYPE_INCOME_DIVIDEND && 
                 this.deal_type !== dealTypes.TYPE_INCOME_COUPON && 
                 this.deal_type !== dealTypes.TYPE_INCOME_AMORTIZATION;
        })
      },
    },
  },
  methods: {
    ...mapActions('investments/deals', ['updateDeal']),
    loadDealData() {
      if (!this.deal) return;
      
      const dealDate = this.$moment.unix(this.deal.created_at);
      this.form_data.date = dealDate.format('YYYY-MM-DD');
      this.form_data.time = dealDate.format('HH:mm');
      
      this.form_data.amount = this.deal.amount;
      this.form_data.note = this.deal.note || '';
      
      // Найти актив по instrument_id
      if (this.deal.instrument && this.deal.instrument.id) {
        const allAssets = this.grouped_assets_list.data.flatMap(group => group.assets || []);
        this.form_data.asset = allAssets.find(asset => asset.instrument && asset.instrument.id === this.deal.instrument.id) || null;
      }
    },
    async submit() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        try {
          this.submitted = true;
          let data = {
            id: this.deal.id,
            portfolio_id: this.current_portfolio.data.id,
            type: this.deal_type,
            instrument_id: this.form_data.asset ? this.form_data.asset.instrument.id : this.deal.instrument.id,
            price: 1,
            amount: +this.form_data.amount,
            note: this.form_data.note,
            date_time: `${this.form_data.date} ${this.form_data.time}:00`,
            timezone: this.$moment.tz.guess(),
          }

          const res = await this.updateDeal(data);
          if (res.body.success) {
            this.$toasted.success('Событие успешно обновлено')
            await this.$store.dispatch('investments/deals/fetchDealsHistory')
            await this.$store.dispatch('investments/assets/getAssets', this.current_portfolio.data.id)
            await this.$store.dispatch('investments/assets/getGroupedAssets', this.current_portfolio.data.id)
            this.$emit('success')
            this.$v.$reset();
          } else {
            this.$toasted.error('Ошибка! Событие не обновлено.')
            console.log(res)
          }
        } catch (err) {
          this.$toasted.error('Ошибка! Событие не обновлено.')
          console.log(err)
        } finally {
          this.submitted = false;
        }
      }
    }
  },
  watch: {
    deal: {
      immediate: true,
      handler(val) {
        if (val) {
          this.loadDealData();
        }
      }
    },
    deal_type(val) {
      if (val === dealTypes.TYPE_INCOME_DIVIDEND || val === dealTypes.TYPE_INCOME_COUPON || val === dealTypes.TYPE_INCOME_AMORTIZATION) {
        this.form_data.asset = null;
      }
      this.$v.$reset();
    },
  },
  i18n: {
    messages: {
      en: {
        valueIncomeLabel: 'Expense sum',
        valueExpenseLabel: 'Income sum',
        assetsLabel: 'Asset',
        noteLabel: 'Note',
        buttonLabel: 'Save',
        today: '[Today]'
      },
      ru: {
        valueIncomeLabel: 'Сумма дохода',
        valueExpenseLabel: 'Сумма расхода',
        assetsLabel: 'Актив',
        noteLabel: 'Заметка',
        buttonLabel: 'Сохранить',
        today: 'Сегодня'
      }
    }
  },
}
</script>

